import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiFetch, ApiError, configureAuth } from "../api/client";
import { clearIdentity, identify } from "../analytics/tracker";

interface User {
  id: string;
  email: string;
  role: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  status: "loading" | "ready";
  // signup non logga più: crea l'account e invia l'email di verifica.
  signup: (email: string, password: string, marketingConsent: boolean | null, locale?: string) => Promise<{ status: string; email: string }>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email: string, locale?: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready">("loading");
  const tokenRef = useRef<string | null>(null);

  // Keep ref in sync for the API client
  useEffect(() => {
    tokenRef.current = accessToken;
  }, [accessToken]);

  const refreshSession = useCallback(async (): Promise<string | null> => {
    try {
      const data = await apiFetch<{ access_token: string }>(
        "/api/v1/auth/refresh",
        { method: "POST" },
        true, // skip retry on this call itself
      );
      setAccessToken(data.access_token);
      tokenRef.current = data.access_token;
      return data.access_token;
    } catch {
      setAccessToken(null);
      setUser(null);
      tokenRef.current = null;
      return null;
    }
  }, []);

  // Wire up the API client's auth hooks
  useEffect(() => {
    configureAuth(
      () => tokenRef.current,
      refreshSession,
    );
  }, [refreshSession]);

  // On mount: try silent refresh then fetch /me
  useEffect(() => {
    (async () => {
      const token = await refreshSession();
      if (token) {
        try {
          const me = await apiFetch<User>("/api/v1/me");
          setUser(me);
          // Anche al ripristino della sessione: chi torna già autenticato non
          // passa dal login, e senza questo la sua navigazione resterebbe
          // anonima per tutta la visita.
          identify(me.id, token, "login");
        } catch {
          setUser(null);
        }
      }
      setStatus("ready");
    })();
  }, [refreshSession]);

  const signup = useCallback(async (email: string, password: string, marketingConsent: boolean | null, locale = "it") => {
    // Nessuna sessione: l'account resta non verificato finché l'utente non
    // conferma l'email cliccando il link ricevuto.
    return await apiFetch<{ status: string; email: string }>(
      "/api/v1/auth/signup",
      { method: "POST", body: JSON.stringify({ email, password, locale, marketing_consent: marketingConsent }) },
      true,
    );
  }, []);

  const verifyEmail = useCallback(async (token: string) => {
    await apiFetch<{ status: string }>(
      "/api/v1/auth/verify-email",
      { method: "POST", body: JSON.stringify({ token }) },
      true,
    );
  }, []);

  const resendVerification = useCallback(async (email: string, locale = "it") => {
    await apiFetch<{ status: string }>(
      "/api/v1/auth/resend-verification",
      { method: "POST", body: JSON.stringify({ email, locale }) },
      true,
    );
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiFetch<{ access_token: string }>(
      "/api/v1/auth/login",
      { method: "POST", body: JSON.stringify({ email, password }) },
      true,
    );
    setAccessToken(data.access_token);
    tokenRef.current = data.access_token;
    const me = await apiFetch<User>("/api/v1/me");
    setUser(me);
    // Collega la navigazione anonima appena conclusa a questa persona: la
    // parte di visita precedente all'accesso è quasi sempre quella in cui si
    // decide l'acquisto, e senza questo resterebbe scollegata da chi compra.
    // Non attende e non può far fallire l'accesso.
    identify(me.id, data.access_token, "login");
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/api/v1/auth/logout", { method: "POST" }, true);
    } catch {
      // ignore
    }
    setAccessToken(null);
    setUser(null);
    tokenRef.current = null;
    // Senza, un secondo accesso dalla stessa scheda con un altro utente non
    // verrebbe collegato: il marcatore riterrebbe il lavoro già fatto.
    clearIdentity();
    window.dispatchEvent(new Event("gb:logout"));
  }, []);

  const value = useMemo(
    () => ({ user, accessToken, status, signup, login, logout, verifyEmail, resendVerification }),
    [user, accessToken, status, signup, login, logout, verifyEmail, resendVerification],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { ApiError };
